export type StartupAutoReloadReason = 'add_trial_granted' | 'locale_mismatch';

const STARTUP_AUTO_RELOAD_STORAGE_KEY = 'EUREKA_STARTUP_AUTO_RELOAD';
const STARTUP_AUTO_RELOAD_INTERVAL = 15 * 1000;

interface StartupAutoReloadRecord {
    reason: StartupAutoReloadReason
    ts: number
}

let startupAutoReloadSettled = false;
let startupAutoReloading = false;
let resolveStartupAutoReloadSettled: () => void = () => undefined;
const startupAutoReloadSettledPromise = new Promise<void>((resolve) => {
    resolveStartupAutoReloadSettled = resolve;
});

function getSessionStorage() {
    if (typeof window === 'undefined') return;
    try {
        return window.sessionStorage;
    } catch (error) {
        console.warn('[StartupAutoReload]', 'sessionStorage is unavailable', error);
    }
}

function readStartupAutoReloadRecord(): StartupAutoReloadRecord | undefined {
    const storage = getSessionStorage();
    if (!storage) return;

    try {
        const raw = storage.getItem(STARTUP_AUTO_RELOAD_STORAGE_KEY);
        if (!raw) return;
        const record = JSON.parse(raw) as StartupAutoReloadRecord;
        if (!record.reason || !Number.isFinite(record.ts)) return;
        return record;
    } catch (error) {
        console.warn('[StartupAutoReload]', 'failed to read reload record', error);
    }
}

function writeStartupAutoReloadRecord(reason: StartupAutoReloadReason) {
    const storage = getSessionStorage();
    if (!storage) return;

    storage.setItem(STARTUP_AUTO_RELOAD_STORAGE_KEY, JSON.stringify({
        reason,
        ts: Date.now(),
    }));
}

export function shouldStartupAutoReload(reason: StartupAutoReloadReason, detail?: Record<string, unknown>) {
    if (typeof window === 'undefined') return false;

    const now = Date.now();
    const record = readStartupAutoReloadRecord();
    if (record && now - record.ts < STARTUP_AUTO_RELOAD_INTERVAL) {
        console.warn('[StartupAutoReload]', 'skip repeated startup reload', {
            reason,
            detail,
            previousReason: record.reason,
            previousTs: record.ts,
        });
        return false;
    }

    writeStartupAutoReloadRecord(reason);
    console.warn('[StartupAutoReload]', 'will reload page', { reason, detail });
    return true;
}

export function markStartupAutoReloading() {
    startupAutoReloading = true;
}

export function isStartupAutoReloading() {
    return startupAutoReloading;
}

export function startupAutoReload(reason: StartupAutoReloadReason, detail?: Record<string, unknown>) {
    const shouldReload = shouldStartupAutoReload(reason, detail);
    if (shouldReload) {
        markStartupAutoReloading();
        window.location.reload();
    }
    return shouldReload;
}

export function markStartupAutoReloadSettled() {
    if (startupAutoReloadSettled) return;
    startupAutoReloadSettled = true;
    resolveStartupAutoReloadSettled();
}

export async function waitStartupAutoReloadSettled() {
    if (!startupAutoReloadSettled) {
        await startupAutoReloadSettledPromise;
    }
    if (!startupAutoReloading) return;
    await new Promise(() => undefined);
}
